/**
 * What a reload gets back: the project that was open (SPEC Scenario D) and the unsaved clip
 * draft that belongs to whatever context the app ends up in.
 *
 * Kept out of `main.ts` so the ordering rule below is testable rather than merely commented.
 * That rule had a hole (audit P0): the restore returned early whenever there was no stored
 * project id, so a draft written with `projectId: null` — the case `DraftClipRecord` explicitly
 * models, i.e. a clip authored before any model was imported — was written correctly every time
 * and offered back never.
 */

import { restoreDraftClip } from './draftClip';
import type { Store } from './store';

/** `settings` key holding the project to reopen on the next load. */
export const LAST_PROJECT_KEY = 'lastProjectId';

/** The `settings` slice this module needs — injectable so the flow is testable without IndexedDB. */
export interface SettingsAccess {
  get<T>(key: string, fallback: T): Promise<T>;
  remove(key: string): Promise<void>;
}

/** The one controller call the restore makes. Narrow on purpose: no controller cycle. */
export interface ProjectOpener {
  openProject(id: string): Promise<void>;
}

export interface SessionRestoreResult {
  /** The project that is open now, or `null` when none was restored. */
  projectId: string | null;
  /** True when a stored draft was opened into the store. */
  draftRestored: boolean;
  /** True when a stored project id existed but could not be opened (and was forgotten). */
  staleProjectId: boolean;
}

/**
 * Reopen the last project, then offer back the draft belonging to the resulting context.
 *
 * Order matters both ways round: the draft restore has to come after the project, because
 * `openProject` writes the clip slice and would undo an `openClip` done before it — and it has to
 * come at all, on every path, because "no project" is a context a draft can belong to.
 */
export async function restoreLastSession(
  store: Store,
  projects: ProjectOpener,
  settings: SettingsAccess,
): Promise<SessionRestoreResult> {
  let id: string | null = null;
  let staleProjectId = false;

  try {
    id = await settings.get<string | null>(LAST_PROJECT_KEY, null);
  } catch (error) {
    console.warn('[intch-motion-kit] could not read the last project id', error);
  }

  if (id) {
    try {
      await projects.openProject(id);
    } catch {
      // `openProject` already toasted and pushed the error; a stale id must not block the app.
      staleProjectId = true;
      id = null;
      try {
        await settings.remove(LAST_PROJECT_KEY);
      } catch {
        /* nothing more to do */
      }
    }
  }

  const draftRestored = await restoreDraftClip(store, id);
  return { projectId: id, draftRestored, staleProjectId };
}
